import React, {useMemo, useState} from 'react';
import {
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	ListSubheader,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/Logout";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import {useTranslation} from "react-i18next";
import TooltipCustom from "@ui/tooltipCustom/TooltipCustom";
import {useAccountStyle} from "@containers/accountStyle/hooks/useAccountStyle";
import {getImpersonationActor} from "@containers/adminAccounts/helpers/impersonation";
import {accountInitials} from "@containers/adminAccounts/helpers/initials";
import {
	useFavoriteAccountQuery,
	useSwitchableAccountsQuery,
	useSwitchAccountQuery
} from "@containers/adminAccounts/hooks/useAdminAccountsQuery";
import {ISwitchableAccount} from "@containers/adminAccounts/types";
import SwitcherUsers from "@containers/adminAccounts/switcher/SwitcherUsers";
import SwitchConfirmBar from "@containers/adminAccounts/switcher/SwitchConfirmBar";
import {STATE_COLORS} from "@containers/adminAccounts/ui/stateColors";

// The header button while connected to another account: the "you are not at home" colour.
const CONNECTED_COLOR = '#f5a623'

type Kind = 'active' | 'multi' | 'inactive'
type Filter = 'all' | Kind

// Fixed colours, not the account accent: the kinds must read the same in every account.
const KIND_COLOR: Record<Kind, string> = {
	active: STATE_COLORS.active,
	multi: STATE_COLORS.multi,
	inactive: STATE_COLORS.inactive,
}

const kindOf = (account: ISwitchableAccount): Kind =>
	!account.active ? 'inactive' : account.multiAccount ? 'multi' : 'active'

// The administrator's own account administers the tenant: violet, as the tenant rows of the system list.
const colorOf = (account: ISwitchableAccount) =>
	account.own && account.active ? STATE_COLORS.tenant : KIND_COLOR[kindOf(account)]

const matches = (account: ISwitchableAccount, query: string) =>
	[account.code, account.description, account.labelName].some((value) => value?.toLowerCase().includes(query))

const byCode = (a: ISwitchableAccount, b: ISwitchableAccount) => a.code.localeCompare(b.code)

interface IProps {
	// Whether to ask track-api for the accounts at all: the React header decides it from the
	// authorities, the legacy header from the page track1 renders.
	enabled: boolean,
}

/**
 * The account the system administrator works in, in the header of every page: opens the list of
 * the tenant's accounts to move to another one or back to their own.
 */
const AccountSwitcherView = ({enabled}: IProps) => {

	const {t} = useTranslation()
	const {tokens} = useAccountStyle()

	// Switching accounts reloads the page, so the cookie read once per load stays current.
	const actor = useMemo(() => getImpersonationActor(), [])

	const {data} = useSwitchableAccountsQuery({enabled})
	const {mutate: switchTo, isPending, variables: pendingAccount} = useSwitchAccountQuery()
	const {mutate: setFavorite} = useFavoriteAccountQuery()

	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [filter, setFilter] = useState<Filter>('all')
	const [usersOf, setUsersOf] = useState<ISwitchableAccount | null>(null)
	// A pick only marks the account; the switch waits for the confirmation.
	const [chosen, setChosen] = useState<ISwitchableAccount | null>(null)

	const accounts = useMemo(() => data?.accounts ?? [], [data])
	const current = accounts.find((account) => account.current)
	const own = accounts.find((account) => account.own)
	const favoritesEnabled = !!data?.favoritesEnabled

	const counts = useMemo(() => accounts.reduce<Record<Filter, number>>((acc, account) => {
		acc.all++
		acc[kindOf(account)]++
		return acc
	}, {all: 0, active: 0, multi: 0, inactive: 0}), [accounts])

	const {favorites, rest} = useMemo(() => {
		const needle = query.trim().toLowerCase()
		const shown = accounts.filter((account) =>
			(filter === 'all' || kindOf(account) === filter) && (!needle || matches(account, needle)))
		return {
			favorites: favoritesEnabled ? shown.filter((account) => account.favorite).sort(byCode) : [],
			rest: shown.filter((account) => !favoritesEnabled || !account.favorite),
		}
	}, [accounts, query, filter, favoritesEnabled])

	if (!data || !current) return null

	const onPick = (account: ISwitchableAccount) => {
		if (account.current || !account.active || isPending) return
		setChosen(account)
	}

	const onClose = () => {
		if (isPending) return
		setOpen(false)
		setQuery('')
		setUsersOf(null)
		setChosen(null)
	}

	// Enter marks the first match, a second Enter confirms it.
	const onSearchKeyDown = (e: React.KeyboardEvent) => {
		if (e.key !== 'Enter' || isPending) return
		if (chosen) {
			switchTo(chosen)
			return
		}
		const first = [...favorites, ...rest].find((account) => !account.current && account.active)
		first && onPick(first)
	}

	const user = actor ? `${actor.username}@${actor.accountCode}` : ''

	const renderAccount = (account: ISwitchableAccount) => {
		const kind = kindOf(account)
		const color = colorOf(account)
		const pending = isPending && pendingAccount?.id === account.id
		return (
			<ListItemButton
				key={account.id}
				selected={account.current || chosen?.id === account.id}
				disabled={isPending && !pending}
				onClick={() => onPick(account)}
				sx={{
					pl: 2.5,
					pr: 1.5,
					py: 0.5,
					gap: 1.5,
					borderLeft: 3,
					borderLeftColor: color,
					cursor: account.current || !account.active ? 'default' : 'pointer',
				}}
			>
				<Avatar variant={'rounded'} sx={{
					width: 30,
					height: 30,
					fontSize: 12,
					fontWeight: 700,
					bgcolor: alpha(color, kind === 'inactive' ? 0.18 : 0.14),
					color: kind === 'inactive' ? 'text.disabled' : color,
				}}>
					{accountInitials(account.code)}
				</Avatar>
				<Stack sx={{minWidth: 0, flex: 1, opacity: kind === 'inactive' ? 0.6 : 1}}>
					<Typography noWrap sx={{fontSize: 14, fontWeight: 600, lineHeight: 1.35}}>
						{account.code}
					</Typography>
					{(account.description || account.labelName) && <Typography
						noWrap
						sx={{fontSize: 12, lineHeight: 1.35}}
						color={'text.secondary'}
					>
						{account.description || account.labelName}
					</Typography>}
				</Stack>
				<Stack direction={'row'} gap={0.5} alignItems={'center'} sx={{flexShrink: 0}}>
					{account.current && <Chip size={'small'} label={t('adminAccounts.switcherCurrent')}/>}
					{account.own && <Chip size={'small'} variant={'outlined'} label={t('adminAccounts.switcherOwn')}/>}
					{kind !== 'active' && <Chip
						size={'small'}
						label={t(kind === 'multi' ? 'adminAccounts.switcherMulti' : 'adminSystem.inactive')}
						sx={{bgcolor: alpha(color, 0.14), color: kind === 'inactive' ? 'text.secondary' : color, fontWeight: 600}}
					/>}
					{pending && <CircularProgress size={16}/>}
					{data?.usersEnabled && account.active && <TooltipCustom title={t('adminAccounts.switcherAsUser')}>
						<IconButton
							size={'small'}
							disabled={isPending}
							onClick={(e) => {
								e.stopPropagation()
								setChosen(null)
								setUsersOf(account)
							}}
							onMouseDown={(e) => e.stopPropagation()}
							sx={{color: 'action.active'}}
						>
							<PersonSearchIcon fontSize={'small'}/>
						</IconButton>
					</TooltipCustom>}
					{favoritesEnabled && <TooltipCustom
						title={t(account.favorite ? 'adminAccounts.switcherUnfavorite' : 'adminAccounts.switcherFavorite')}
					>
						<IconButton
							size={'small'}
							onClick={(e) => {
								e.stopPropagation()
								setFavorite({id: account.id, favorite: !account.favorite})
							}}
							onMouseDown={(e) => e.stopPropagation()}
							sx={{color: account.favorite ? '#f5b400' : 'action.disabled'}}
						>
							{account.favorite ? <StarIcon fontSize={'small'}/> : <StarBorderIcon fontSize={'small'}/>}
						</IconButton>
					</TooltipCustom>}
				</Stack>
			</ListItemButton>
		)
	}

	const filters: Filter[] = ['all', 'active', 'multi', 'inactive']

	return (
		<>
			<TooltipCustom
				title={actor
					? t('adminAccounts.switcherConnectedTooltip', {code: current.code, user})
					: t('adminAccounts.switcherTooltip')}
			>
				<Button
					onClick={() => setOpen(true)}
					startIcon={actor
						? <Box component={'span'} sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: CONNECTED_COLOR}}/>
						: <SwapHorizIcon fontSize={'small'}/>}
					size={'small'}
					sx={{
						textTransform: 'none',
						fontWeight: 600,
						maxWidth: 320,
						px: 1.25,
						borderRadius: 2,
						color: tokens.text,
						border: 1,
						borderColor: actor ? CONNECTED_COLOR : alpha(tokens.text, 0.35),
						// The legacy header drops the border for its pill, so the ring is drawn as a shadow too.
						boxShadow: actor ? `inset 0 0 0 1.5px ${CONNECTED_COLOR}` : 'none',
						bgcolor: 'transparent',
						'&:hover': {bgcolor: alpha(tokens.text, 0.16)},
					}}
				>
					<Box component={'span'} sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
						{current.code}
					</Box>
					{actor && <Box component={'span'} sx={{
						ml: 0.75,
						flexShrink: 0,
						whiteSpace: 'nowrap',
						fontWeight: 400,
						color: alpha(CONNECTED_COLOR, 0.9),
					}}>
						· {actor.login}
					</Box>}
				</Button>
			</TooltipCustom>

			<Dialog
				open={open}
				onClose={onClose}
				maxWidth={'sm'}
				fullWidth
				PaperProps={{sx: {borderRadius: 4, overflow: 'hidden', height: 'min(680px, 85vh)'}}}
			>
				{usersOf ? <SwitcherUsers account={usersOf} onBack={() => setUsersOf(null)}/> : <>
				<Stack sx={{background: tokens.panelBackground, color: tokens.text, px: 3, pt: 2.5, pb: 2}} gap={1.5}>
					<Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
						<Stack sx={{minWidth: 0}}>
							<Typography sx={{fontSize: 18, fontWeight: 700, lineHeight: 1.3}}>
								{t('adminAccounts.switcherTitle')}
							</Typography>
							<Typography sx={{fontSize: 13, lineHeight: 1.4, color: tokens.textMuted, overflowWrap: 'break-word'}}>
								{actor
									? t('adminAccounts.switcherConnectedAs', {code: current.code, user})
									: t('adminAccounts.switcherYouAreIn', {code: current.code})}
							</Typography>
						</Stack>
						{own && !own.current && <Button
							variant={'contained'}
							size={'small'}
							disableElevation
							disabled={isPending}
							startIcon={isPending && pendingAccount?.own
								? <CircularProgress size={16} color={'inherit'}/>
								: <LogoutIcon fontSize={'small'}/>}
							onClick={() => switchTo(own)}
							sx={{
								textTransform: 'none',
								flexShrink: 0,
								bgcolor: '#fff',
								color: 'grey.900',
								'&:hover': {bgcolor: 'grey.200'},
							}}
						>
							{t('adminAccounts.release', {account: own.code})}
						</Button>}
					</Stack>
					<TextField
						autoFocus
						size={'small'}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={onSearchKeyDown}
						placeholder={t('adminAccounts.switcherSearch')}
						InputProps={{
							startAdornment: <InputAdornment position={'start'}><SearchIcon fontSize={'small'}/></InputAdornment>,
							sx: {bgcolor: 'background.paper', borderRadius: 2},
						}}
					/>
					<Stack direction={'row'} gap={0.75} flexWrap={'wrap'}>
						{filters.map((value) => {
							const selected = filter === value
							return <Chip
								key={value}
								size={'small'}
								onClick={() => setFilter(value)}
								icon={value === 'all' ? undefined : <Box component={'span'} sx={{
									width: 8,
									height: 8,
									borderRadius: '50%',
									bgcolor: KIND_COLOR[value],
									ml: '8px !important',
								}}/>}
								label={`${t(`adminAccounts.switcherFilter.${value}`)} ${counts[value]}`}
								sx={{
									fontWeight: 600,
									color: selected ? 'grey.900' : tokens.text,
									bgcolor: selected ? '#fff' : alpha(tokens.text, 0.12),
									'&:hover': {bgcolor: selected ? 'grey.200' : alpha(tokens.text, 0.22)},
								}}
							/>
						})}
					</Stack>
				</Stack>

				<List dense disablePadding sx={{overflowY: 'auto', flex: 1, pb: 0.5}}>
					{favoritesEnabled && !favorites.length && !query && filter === 'all' && <Typography
						sx={{px: 3, py: 1, fontSize: 12}}
						color={'text.secondary'}
					>
						{t('adminAccounts.switcherFavoritesHint')}
					</Typography>}
					{favorites.length > 0 && <>
						<ListSubheader sx={{lineHeight: '32px', fontSize: 12, fontWeight: 700}}>
							{t('adminAccounts.switcherFavorites')}
						</ListSubheader>
						{favorites.map(renderAccount)}
						<ListSubheader sx={{lineHeight: '32px', fontSize: 12, fontWeight: 700}}>
							{t('adminAccounts.switcherAll')}
						</ListSubheader>
					</>}
					{rest.map(renderAccount)}
					{!favorites.length && !rest.length && <Typography sx={{px: 3, py: 2, fontSize: 14}} color={'text.secondary'}>
						{t('adminAccounts.switcherNothing')}
					</Typography>}
				</List>
				{chosen && <SwitchConfirmBar
					text={t('adminAccounts.switcherConfirm', {code: chosen.code})}
					pending={isPending}
					onCancel={() => setChosen(null)}
					onConfirm={() => switchTo(chosen)}
				/>}
				</>}
			</Dialog>
		</>
	);
};

export default AccountSwitcherView;
